import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requirePrimaryUser?: boolean;
}

interface UserInfo {
  id: string;
  userName: string;
  isPrimaryUser: boolean;
  token: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requirePrimaryUser = false,
}) => {
  const decodeJWT = (token: string): any | null => {
    try {
      // Validate JWT structure first
      const parts = token.split(".");
      if (parts.length !== 3) {
        // Token is likely opaque (e.g., Google token), not a standard JWT
        return null;
      }

      // JWT uses base64url encoding, not standard base64
      const base64UrlDecode = (str: string) => {
        str = str.replace(/-/g, "+").replace(/_/g, "/");
        while (str.length % 4) {
          str += "=";
        }
        return atob(str);
      };

      const decoded = base64UrlDecode(parts[1]);
      const payload = JSON.parse(decoded);
      return payload;
    } catch (error) {
      // Token is likely opaque or corrupted, silently return null
      return null;
    }
  };

  const [userInfo, setUserInfo] = useState<UserInfo | null>(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const payload = decodeJWT(token);
      if (payload) {
        // Backend JWT
        const isGoogleToken = payload.iss && payload.iss.includes("google.com");

        let isPrimaryUser = true; // default
        if (isGoogleToken) {
          isPrimaryUser = true; // Google users are always primary
        } else if (payload.isPrimaryUser !== undefined) {
          isPrimaryUser = payload.isPrimaryUser;
        }

        return {
          id: payload.sub || payload.id || "authenticated-user",
          userName: payload.name || payload.userName || "User",
          isPrimaryUser: isPrimaryUser,
          token: token,
        };
      } else {
        // Assume Google auth (opaque token)
        return {
          id: "google-user",
          userName: "Google User",
          isPrimaryUser: true,
          token: token,
        };
      }
    }
    return null;
  });

  useEffect(() => {
    const checkToken = () => {
      const token = localStorage.getItem("token");
      if (token) {
        const payload = decodeJWT(token);
        if (payload) {
          // Backend JWT
          const isGoogleToken =
            payload.iss && payload.iss.includes("google.com");

          let isPrimaryUser = true; // default
          if (isGoogleToken) {
            isPrimaryUser = true; // Google users are always primary
          } else if (payload.isPrimaryUser !== undefined) {
            isPrimaryUser = payload.isPrimaryUser;
          }

          setUserInfo({
            id: payload.sub || payload.id || "authenticated-user",
            userName: payload.name || payload.userName || "User",
            isPrimaryUser: isPrimaryUser,
            token: token,
          });
        } else {
          // Assume Google auth (opaque token)
          setUserInfo({
            id: "google-user",
            userName: "Google User",
            isPrimaryUser: true,
            token: token,
          });
        }
      } else {
        setUserInfo(null);
      }
    };

    // Listen for token changes from other tabs or same tab
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "token" || event.key === null) {
        // event.key === null means clear() was called
        checkToken();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  if (!userInfo) {
    return <Navigate to="/login" replace />;
  }

  // If primary user access is required but user is not primary user, redirect to home
  if (requirePrimaryUser && userInfo && !userInfo.isPrimaryUser) {
    return <Navigate to="/home" replace />;
  }

  // If we reach here, user has the required permissions
  return <>{children}</>;
};

export default ProtectedRoute;
